"use client";

import { ErrorDisplayInfo } from "@/lib/error-logging/error-types";
import { useErrorLogger } from "@/lib/error-logging/use-error-logger";
import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";

export default function Error({
    error,
    reset
}: {
    error: Error & { digest?: string };
    reset: () => void;
}) {
    const { data: session } = useSession();
    const { logReactError } = useErrorLogger();
    const [errorInfo, setErrorInfo] = useState<ErrorDisplayInfo | null>(null);
    const [isLogging, setIsLogging] = useState(false);

    useEffect(() => {
        const logErrorAndSetInfo = async () => {
            setIsLogging(true);

            // Log the error if user is authenticated
            const response = await logReactError(error, {}, "high");

            // Generate display info for screenshot reference
            const displayInfo: ErrorDisplayInfo = {
                errorId: response?.errorId || `local_${Date.now()}`,
                timestamp: new Date().toLocaleString(),
                userInfo: session
                    ? `User: ${session.user.userID} | Role: ${session.user.role} | Customer: ${session.currentCustomerID || "None"}`
                    : "Not authenticated",
                browserInfo: getBrowserInfo(),
                sanitizedMessage: getSanitizedMessage(error.message)
            };

            setErrorInfo(displayInfo);
            setIsLogging(false);
        };

        logErrorAndSetInfo();
    }, [error, session, logReactError]);

    const copyErrorInfo = () => {
        if (!errorInfo) return;

        const errorDetails = `Error Report
ID: ${errorInfo.errorId}
Time: ${errorInfo.timestamp}
${errorInfo.userInfo}
Browser: ${errorInfo.browserInfo}
Message: ${errorInfo.sanitizedMessage}
URL: ${window.location.href}`;

        navigator.clipboard
            ?.writeText(errorDetails)
            .then(() => {
                alert("Error details copied to clipboard");
            })
            .catch(() => {
                console.log("Failed to copy to clipboard");
            });
    };

    return (
        <div className="mx-auto my-4 flex max-w-xl flex-col rounded-lg border border-neutral-200 bg-white p-8 dark:border-neutral-800 dark:bg-black md:p-12">
            <h2 className="text-xl font-bold text-red-600">Application Error</h2>

            <p className="my-2 text-gray-700 dark:text-gray-300">
                Something went wrong with the application. This error has been{" "}
                {session ? "logged" : "captured"} and our team will investigate.
            </p>

            {errorInfo && (
                <div className="my-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-900">
                    <h3 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
                        Error Details (for support reference):
                    </h3>

                    <div className="space-y-1 font-mono text-xs text-gray-600 dark:text-gray-400">
                        <div>
                            <span className="font-semibold">ID:</span> {errorInfo.errorId}
                        </div>
                        <div>
                            <span className="font-semibold">Time:</span> {errorInfo.timestamp}
                        </div>
                        <div>
                            <span className="font-semibold">User:</span> {errorInfo.userInfo}
                        </div>
                        <div>
                            <span className="font-semibold">Browser:</span> {errorInfo.browserInfo}
                        </div>
                        <div>
                            <span className="font-semibold">Page:</span> {window.location.pathname}
                        </div>
                        {errorInfo.sanitizedMessage && (
                            <div>
                                <span className="font-semibold">Error:</span>{" "}
                                {errorInfo.sanitizedMessage}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={copyErrorInfo}
                        className="mt-2 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                        Copy error details
                    </button>
                </div>
            )}

            {isLogging && (
                <div className="my-2 text-sm text-gray-500">Logging error details...</div>
            )}

            <div className="mt-4 flex gap-3">
                <button
                    className="flex-1 rounded-full bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                    onClick={() => reset()}
                >
                    Try Again
                </button>

                <button
                    className="flex-1 rounded-full bg-gray-200 px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    onClick={() => (window.location.href = "/")}
                >
                    Go Home
                </button>
            </div>

            <p className="mt-4 text-center text-xs text-gray-500">
                If this problem persists, please contact support with the error ID above.
            </p>
        </div>
    );
}

function getBrowserInfo(): string {
    const ua = navigator.userAgent;
    let browser = "Unknown";
    let version = "";

    if (ua.includes("Chrome")) {
        const match = ua.match(/Chrome\/([0-9.]+)/);
        browser = "Chrome";
        version = match && match[1] ? match[1] : "";
    } else if (ua.includes("Firefox")) {
        const match = ua.match(/Firefox\/([0-9.]+)/);
        browser = "Firefox";
        version = match && match[1] ? match[1] : "";
    } else if (ua.includes("Safari") && !ua.includes("Chrome")) {
        const match = ua.match(/Safari\/([0-9.]+)/);
        browser = "Safari";
        version = match && match[1] ? match[1] : "";
    } else if (ua.includes("Edge")) {
        const match = ua.match(/Edge\/([0-9.]+)/);
        browser = "Edge";
        version = match && match[1] ? match[1] : "";
    }

    return `${browser}${version ? ` ${version.split(".")[0]}` : ""}`;
}

function getSanitizedMessage(message: string): string {
    if (!message) return "Unknown error occurred";

    // Show first part of error message, remove potentially sensitive info
    return message
        .replace(/password[=:]\s*[^\s&]+/gi, "password=***")
        .replace(/token[=:]\s*[^\s&]+/gi, "token=***")
        .substring(0, 100);
}
